/**
 * Shared CSR-style vertex pack/unpack for the LWPOLYLINE-shaped compact stores
 * (LWPOLYLINE + REVCLOUD). Both persist their boundary vertices identically —
 * 5 floats per vertex (x, y, bulge, startWidth, endWidth) into a growable
 * `Float32Array`, with an optional per-entity `vertexId` array kept sparsely in
 * a `Map`. Extracted as the single source of truth so the two stores share one
 * implementation instead of byte-for-byte copies (jscpd one-truth extraction).
 *
 * `PolylineVertex` (drawing-model) is the on-disk vertex shape; only the five
 * packed scalars + optional `vertexId` participate.
 *
 * STRUCTURAL-EQUIVALENCE NOTE: this is a SINGLE-TRUTH share, NOT a structural
 * cast. Both LWPOLYLINE (`lwpolylineEntitySchema.vertices`) and REVCLOUD
 * (`revisionCloudEntitySchema.vertices`) declare their vertices as
 * `z.array(polylineVertexSchema)` in model.ts — i.e. they are the SAME
 * `PolylineVertex` type, field-identical by construction. No `as` cast bridges
 * two distinct shapes here; both stores import this one packer. If REVCLOUD
 * ever forks its own vertex schema, this share must be re-evaluated (split the
 * packer or re-prove field identity) rather than silently casting.
 */

import type { PolylineVertex } from "@dxf-ai-editor/drawing-model";

/** Floats per packed vertex — x, y, bulge, startWidth, endWidth. */
export const POLYLINE_VERTEX_FLOATS = 5;

/** Minimal CSR vertex-buffer surface shared by LWPOLYLINE / REVCLOUD stores. */
export interface PolylineVertexColumns {
  vertexData: Float32Array;
  vertexOffsets: Uint32Array;
  vertexIdData: Map<number, number[]>;
}

/** Grow a Float32Array (doubling) to hold at least `needed` floats. */
function ensureFloatCap(buf: Float32Array, needed: number): Float32Array {
  if (needed <= buf.length) return buf;
  let cap = Math.max(buf.length, 1);
  while (cap < needed) cap *= 2;
  const grown = new Float32Array(cap);
  grown.set(buf);
  return grown;
}

/**
 * Pack `entity.vertices` for the entity at `index` into the store's CSR vertex
 * buffer, growing it as needed and recording the [start, end) slice on
 * `vertexOffsets`. A per-vertex `vertexId` array is stored on `vertexIdData`
 * only when at least one vertex carries an id (absent ids round-trip as -1).
 */
export function packPolylineVertices(
  store: PolylineVertexColumns,
  index: number,
  vertices: readonly PolylineVertex[],
): void {
  const startVertex = index === 0 ? 0 : store.vertexOffsets[index]!;
  const vertexFloats = vertices.length * POLYLINE_VERTEX_FLOATS;
  store.vertexData = ensureFloatCap(
    store.vertexData,
    startVertex * POLYLINE_VERTEX_FLOATS + vertexFloats,
  );
  let hasVertexIds = false;
  for (const v of vertices) {
    if (v.vertexId !== undefined) {
      hasVertexIds = true;
      break;
    }
  }
  for (let i = 0; i < vertices.length; i++) {
    const v = vertices[i]!;
    const off = (startVertex + i) * POLYLINE_VERTEX_FLOATS;
    store.vertexData[off + 0] = v.x;
    store.vertexData[off + 1] = v.y;
    store.vertexData[off + 2] = v.bulge;
    store.vertexData[off + 3] = v.startWidth;
    store.vertexData[off + 4] = v.endWidth;
  }
  store.vertexOffsets[index] = startVertex;
  store.vertexOffsets[index + 1] = startVertex + vertices.length;
  if (hasVertexIds) {
    const ids: number[] = [];
    for (const v of vertices) ids.push(v.vertexId ?? -1);
    store.vertexIdData.set(index, ids);
  }
}

/**
 * Reconstruct the vertex array for the entity at `index` from the store's CSR
 * vertex buffer. A `vertexId` is attached only when one was recorded for that
 * slot (sentinel -1 ⇒ absent).
 */
export function unpackPolylineVertices(
  store: PolylineVertexColumns,
  index: number,
): PolylineVertex[] {
  const startVertex = store.vertexOffsets[index]!;
  const endVertex = store.vertexOffsets[index + 1]!;
  const ids = store.vertexIdData.get(index);
  const vertices: PolylineVertex[] = [];
  for (let i = 0; i < endVertex - startVertex; i++) {
    const off = (startVertex + i) * POLYLINE_VERTEX_FLOATS;
    const vertex: PolylineVertex = {
      x: store.vertexData[off + 0]!,
      y: store.vertexData[off + 1]!,
      bulge: store.vertexData[off + 2]!,
      startWidth: store.vertexData[off + 3]!,
      endWidth: store.vertexData[off + 4]!,
    };
    if (ids !== undefined && ids[i] !== undefined && ids[i] !== -1) {
      vertex.vertexId = ids[i]!;
    }
    vertices.push(vertex);
  }
  return vertices;
}
